import React, { useEffect, useMemo, useState } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import TeamData from "./TeamData.json";
import { Link } from "react-router-dom";
import "./TeamPage.css";

function TeamPage() {
  document.title = "Leadership Advisors :: D-Right Technologies";

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Get CEO data from team_member_detail
  const ceoData = TeamData.leadership_team?.team_member_detail?.find(
    (member) => member.role === "CEO"
  );

  // Get leadership team members excluding CEO
  const leadershipMembers = TeamData.leadership_team?.team_members?.filter(
    (member) => member.role !== "CEO"
  ) || [];

  // Get Advisory Board members from TeamData
  const advisors = TeamData.advisory_board || [];

  const ceoFocusAreas = useMemo(() => {
    const items = ceoData?.activities_skills || [];
    return items.slice(0, 6);
  }, [ceoData]);

  const ceoStrengths = useMemo(() => {
    const items = ceoData?.professional_skills || [];
    return items.slice(0, 4);
  }, [ceoData]);

  const ceoDescriptionMobile = useMemo(() => {
    const desc = ceoData?.description || "";
    const idx = desc.indexOf("success of the company.");
    if (idx !== -1) return desc.substring(0, idx + "success of the company.".length);
    return desc.split(". ")[0] + ".";
  }, [ceoData]);

  // Advisory Board card – name/title in front of icon, separator, desc below
  // No detail page for advisory board for now – Link commented out
  const renderAdvisorCard = (advisor, index) => (
    <div className="col-12 col-sm-6 col-lg-4 mb-3" key={index}>
      {/* <Link to={`/team/detail/${advisor.slug}`} style={{ textDecoration: "none", display: "block", height: "100%" }}> */}
      <div
        style={{ display: "block", height: "100%", cursor: "default" }}
      >
        <div
          className="advisory-card h-100"
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #e8edf3",
            transition: "all 0.3s ease",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            padding: "14px 16px",
            display: "flex",
            flexDirection: "column"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(78, 143, 90, 0.12)";
            e.currentTarget.style.borderColor = "#4E8F5A";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.05)";
            e.currentTarget.style.borderColor = "#e8edf3";
          }}
        >
          {/* Image left, name & title to the right */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "12px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                overflow: "hidden",
                flexShrink: 0,
                background: "#f0f4f8"
              }}
            >
              <img
                src={advisor.image}
                alt={advisor.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top center"
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h5 style={{ color: "var(--brand-green-dark, #2C5E47)", fontWeight: "700", fontSize: "0.95rem", marginBottom: "2px", lineHeight: 1.3 }}>
                {advisor.name}
              </h5>
              <p style={{ color: "#4E8F5A", fontWeight: "600", fontSize: "0.75rem", marginBottom: 0 }}>
                {advisor.role}
              </p>
            </div>
          </div>
          {/* Line separator */}
          <div style={{ borderTop: "1px solid rgba(78, 143, 90, 0.35)", marginBottom: "10px" }} />
          {/* Description below */}
          {advisor.description && (
            <p style={{ color: "#555", fontSize: "0.8rem", lineHeight: 1.5, marginBottom: 0, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {advisor.description}
            </p>
          )}
        </div>
      </div>
      {/* </Link> */}
    </div>
  );

  return (
    <>
      <Header />
      <div className="main no-banner-top-padding">
        <section
          className="page-header-section ptb-100 bg-image"
          image-overlay="7"
        >
          <div
            className="background-image-wraper"
            style={{
              background: `url('assets/img/team-banner.jpg')`,
              opacity: "1",
            }}
          ></div>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-9 col-lg-7">
                <div className="page-header-content text-white pt-4">
                  <h1 className="text-white mb-0">Leadership & Advisors</h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CEO Section - Enhanced (keep gray theme) */}
        {ceoData && (
          <section
            className="ptb-100 leadership-section"
            style={{
              background: "#f8f9fa",
              paddingTop: "90px",
            }}
          >
          <div className="container">
              {/* Leadership Heading and Description – match LARIS "Optimized for End-to-End Workflow" layout */}
              <div className="row justify-content-center">
                <div className="col-md-10 col-lg-10 mb-4 leadership-heading-wrap" style={{ textAlign: "center" }}>
                  <h2 
                    className="web mb-3"
                    style={{
                      fontSize: "2.8rem",
                      fontWeight: "800",
                      color: "#2d3748",
                      marginBottom: "20px"
                    }}
                  >
                    Visionary Leadership
                  </h2>
                  <p style={{ marginBottom: "0" }}>
                    Our leadership team brings together diverse expertise and strategic vision 
                    to guide D-Right Technologies toward continued growth and innovation.
                  </p>
                </div>
              </div>
              {/* Layout: Banner */}
              <div className="row justify-content-center">
                <div className="col-lg-11">
                  <Link
                    to={`/team/detail/${ceoData.slug}`}
                    style={{ textDecoration: "none", display: "block", width: "100%" }}
                  >
                    <div
                      className="bg-white ceo-card-link"
                      style={{
                        borderRadius: 22,
                        border: "1px solid #e2e8f0",
                        overflow: "hidden",
                        boxShadow: "0 14px 40px rgba(0,0,0,0.06)",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 18px 50px rgba(0,0,0,0.1)";
                        e.currentTarget.style.borderColor = "#4E8F5A";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "0 14px 40px rgba(0,0,0,0.06)";
                        e.currentTarget.style.borderColor = "#e2e8f0";
                      }}
                    >
                      <div className="row g-0">
                        <div className="col-md-5">
                          <div
                            className="ceo-banner-image"
                            style={{
                              width: "100%",
                              height: "100%",
                              minHeight: 320,
                              backgroundImage: `url(${ceoData.image})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                        </div>
                        <div className="col-md-7">
                          <div className="ceo-card-content" style={{ padding: 26 }}>
                            <div className="ceo-role-label" style={{ fontSize: 12, fontWeight: 900, color: "#4E8F5A", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              Chief Executive Officer
                            </div>
                            <h2 className="ceo-name" style={{ fontSize: "2.4rem", fontWeight: 900, color: "#2d3748", marginBottom: 12 }}>
                              {ceoData.name}
                            </h2>
                            <p style={{ color: "#4a5568", lineHeight: 1.9, marginBottom: 0 }}>
                              {isMobile ? ceoDescriptionMobile : ceoData.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>

            {/* Leadership Team Cards */}
            {leadershipMembers.length > 0 && (
              <div className="row justify-content-center mt-5">
                {/* Match CEO card width/margins (col-lg-11), but reduce ONLY the gap between the 2 cards */}
                <div className="col-lg-11">
                  <div className="row g-2 justify-content-center">
                    {leadershipMembers.map((member, index) => (
                      <div className="col-12 col-md-6" key={index}>
                        <Link
                          to={`/team/detail/${member?.slug}`}
                          style={{ textDecoration: "none", display: "block", width: "100%" }}
                        >
                          <div
                            className="h-100"
                            style={{
                              background: "#ffffff",
                              borderRadius: "20px",
                              padding: "30px",
                              border: "1px solid #e2e8f0",
                              transition: "all 0.3s ease",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                              height: "100%",
                              minHeight: "350px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-10px)";
                              e.currentTarget.style.boxShadow = "0 15px 40px rgba(78, 143, 90, 0.2)";
                              e.currentTarget.style.borderColor = "#4E8F5A";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.05)";
                              e.currentTarget.style.borderColor = "#e2e8f0";
                            }}
                          >
                            <div
                              className="mb-4"
                              style={{
                                width: "180px",
                                height: "180px",
                                borderRadius: "50%",
                                backgroundImage: `url(${member.image})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                border: "5px solid #f7fafc",
                                boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                                flexShrink: 0,
                              }}
                            ></div>
                            <h4
                              className="mb-2"
                              style={{
                                color: "#2d3748",
                                fontWeight: "700",
                                fontSize: "1.4rem",
                                textAlign: "center",
                              }}
                            >
                              {member.name}
                            </h4>
                            <p
                              className="mb-0"
                              style={{
                                color: "#4E8F5A",
                                fontWeight: "600",
                                fontSize: "1rem",
                                textAlign: "center",
                              }}
                            >
                              {member.role}
                            </p>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          </section>
        )}

        {/* Advisory Board Section */}
        <section 
          className="ptb-100 advisory-board-section" 
          style={{ background: "#f8f9fa" }}
        >
          <div className="container">
            {/* Advisory Board heading – match LARIS "Optimized for End-to-End Workflow" layout */}
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-10 advisory-heading-wrap" style={{ textAlign: "center", marginBottom: "0.5rem" }}>
                <h2 
                  className="web mb-3"
                  style={{
                    fontSize: "2.8rem",
                    fontWeight: "800",
                    color: "#2d3748",
                    marginBottom: "20px"
                  }}
                >
                  Advisory Board
                </h2>
                <p style={{ marginBottom: "0" }}>
                  The panel of experts from the IT and healthcare industries who provide guidance, 
                  expertise, and strategic advice form our Advisory Board. This segment plays a 
                  critical role in offering insights and recommendations to ensure the project aligns 
                  with industry standards and best practices.
                </p>
              </div>
            </div>
            <div 
              className="row justify-content-center advisory-cards-row g-4"
              style={{
                marginLeft: "0",
                marginRight: "0",
                maxWidth: "100%",
                marginTop: "1rem"
              }}
            >
              {advisors.map((advisor, index) => renderAdvisorCard(advisor, index))}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default TeamPage;
